import { useCallback, useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { v4 as uuidv4 } from 'uuid'
import toast from 'react-hot-toast'
import { useFeature } from '../../providers/featureFlags'
import { useClient, useSession } from '../../providers/supabase'
import { useAppSelector, useAppDispatch } from '../../hooks/reduxToolkit'
import { useSentry } from '../../providers/sentry'
import { hasAtLeastFiveBusinessDaysPassed, fiveBusinessDaysFrom } from '../../../lib/util/itemizedBillRequestLogic'
import { normalizePhoneNumber } from '../../../lib/util/normalizePhoneNumber'
import { present } from '../../../lib/util/present'
import { selectTodosForExpense } from '../../reduxToolkit/selectors/todos'
import { isOpenTodo } from '../../reduxToolkit/todosSlice'
import { selectConversationsWithNestedDataForProvider, findItemizedBillConversation, type ConversationWithMessagesAndPhoneCalls } from '../../reduxToolkit/conversationsSelectors'
import { addConversation, updateConversation, addPhoneCall } from '../../reduxToolkit/conversationsSlice'
import { updateExpense } from '../../reduxToolkit/expensesSlice'
import { NotFound } from '../../screens/notFound'
import { useAsyncEffect } from '../../hooks/useAsyncEffect'
import { useSubscriptionPrompt } from '../../hooks/useSubscriptionPrompt'
import { formatDateInTimeZone } from '../../../lib/formatDateInTimeZone'

type DisabledReason =
  | 'no_phone'
  | 'not_authorized'
  | 'not_whitelisted'
  | 'no_open_todo'
  | 'date_of_service_too_recent'
  | null

interface RequirementStatus {
  id: string
  label: string
  satisfied: boolean
  explanation: string
}

function getDisabledReason(
  providerPhone: string | null | undefined,
  isAuthorized: boolean,
  isWhitelisted: boolean,
  hasOpenItemizedBillTodo: boolean,
  dateOfService: string | null | undefined
): DisabledReason {
  if (!providerPhone) {
    return 'no_phone'
  }
  if (!isAuthorized) {
    return 'not_authorized'
  }
  if (!isWhitelisted) {
    return 'not_whitelisted'
  }
  if (!hasOpenItemizedBillTodo) {
    return 'no_open_todo'
  }

  // Check if date of service is at least 5 business days ago
  if (dateOfService && !hasAtLeastFiveBusinessDaysPassed(new Date(dateOfService))) {
    return 'date_of_service_too_recent'
  }

  return null
}

function buildRequirements(
  providerPhone: string | null | undefined,
  isAuthorized: boolean,
  isWhitelisted: boolean,
  hasOpenItemizedBillTodo: boolean,
  dateOfService: string | null | undefined
): RequirementStatus[] {
  const requirements: RequirementStatus[] = []

  // Phone number requirement
  requirements.push({
    id: 'phone',
    label: 'Provider has phone number',
    satisfied: present(providerPhone),
    explanation: present(providerPhone)
      ? `Phone: ${providerPhone}`
      : 'The provider must have a phone number on file to receive AI calls.',
  })

  // Authorization requirement
  requirements.push({
    id: 'authorized',
    label: 'Communication authorized',
    satisfied: isAuthorized,
    explanation: isAuthorized
      ? 'You have authorized communication with this provider.'
      : 'You must authorize communication with this provider before placing AI calls. Go to the provider page to authorize.',
  })

  // Whitelist requirement
  requirements.push({
    id: 'whitelisted',
    label: 'Provider whitelisted for AI calling',
    satisfied: isWhitelisted,
    explanation: isWhitelisted
      ? 'This provider is approved for AI phone calls.'
      : 'This provider must be whitelisted for AI calling. Contact your plan administrator.',
  })

  // Open TODO requirement
  requirements.push({
    id: 'open_todo',
    label: 'Open itemized bill request',
    satisfied: hasOpenItemizedBillTodo,
    explanation: hasOpenItemizedBillTodo
      ? 'There is an open itemized bill request for this expense.'
      : 'There must be an open itemized bill request TODO for this expense.',
  })

  // Date of service requirement
  const dateOfServiceSatisfied = dateOfService
    ? hasAtLeastFiveBusinessDaysPassed(new Date(dateOfService))
    : false

  let dateOfServiceExplanation: string
  if (!dateOfService) {
    dateOfServiceExplanation = 'Date of service is required.'
  } else if (dateOfServiceSatisfied) {
    dateOfServiceExplanation = 'At least 5 business days have passed since the date of service.'
  } else {
    const dueDate = fiveBusinessDaysFrom(new Date(dateOfService))
    dateOfServiceExplanation = `AI calling will be available on ${formatDateInTimeZone(dueDate.toISOString(), { format: 'MM/dd/yyyy' })} (5 business days after the date of service).`
  }

  requirements.push({
    id: 'date_of_service',
    label: 'Date of service at least 5 business days ago',
    satisfied: dateOfServiceSatisfied,
    explanation: dateOfServiceExplanation,
  })

  return requirements
}

export function AICallScreen() {
  const { providerId } = useParams<{ providerId: string }>()
  const [searchParams] = useSearchParams()
  const expenseId = searchParams.get('expenseId')
  const navigate = useNavigate()
  const client = useClient()
  const session = useSession()
  const sentry = useSentry()
  const isAiPhoneCallingEnabled = useFeature('ai_phone_calling')

  // Pre-signup trial users reach this screen (useFeature grants them everything)
  // so they can see what AI calling does, but placing a call requires a plan —
  // the backend cancels any call from a membership with no subscription and no
  // org flag (OrganizationFeatureFlagService::membershipHasFlagViaOrgOrTier).
  const { checkSubscription, SubscriptionPromptModal } = useSubscriptionPrompt({
    trigger: 'always',
    title: 'Subscribe for AI Phone Calls',
    message:
      'Alice (our AI agent) can call this provider on your behalf and request your itemized bill. Subscribe to a plan to place the call.',
  })

  const userId = useAppSelector((s) => s.membership.userId)
  const dispatch = useAppDispatch()
  const membershipId = useAppSelector((s) => s.membership.membershipId)
  const provider = useAppSelector((s) => s.providers?.providers.find((p) => p.id === providerId))
  const expense = useAppSelector((s) => s.expenses.expenses.find((e) => e.id === expenseId))
  const profile = useAppSelector((s) => s.membership.profile)

  const [isRequesting, setIsRequesting] = useState(false)

  // Editable parameters state
  const [parameters, setParameters] = useState<Record<string, string>>({})

  // Initialize parameters from expense data
  // Dependencies are intentionally based on identity values (ids, email, phone, address)
  // rather than all properties to avoid resetting user edits during data refreshes
  useEffect(() => {
    if (expense && provider && session?.user && profile) {
      const initial: Record<string, string> = {}
      if (expense.patient_name) initial.patient_name = expense.patient_name
      if (provider.name) initial.provider_name = provider.name
      if (expense.date) initial.date_of_service = expense.date
      if (expense.paidAmount) initial.total_amount = `$${expense.paidAmount}`
      if (expense.patient_dob) initial.patient_dob = expense.patient_dob
      if (session.user.email) initial.patient_email = session.user.email
      if (profile.phone) initial.patient_phone = profile.phone
      if (profile.address) initial.patient_address = profile.address
      setParameters(initial)
    }
  }, [expense?.id, provider?.id, session?.user?.email, profile?.phone, profile?.address])

  // Whitelist check. AI calling is allowed by default; a provider is only
  // blocked once an admin explicitly disables its number (revoked_at set).
  // The RLS policy only exposes disabled rows, so the absence of a row means
  // the provider is enabled. Therefore we default isWhitelisted to true and
  // only set it to false when a disabled row is returned.
  const [isWhitelisted, setIsWhitelisted] = useState(true)
  const { loading: isWhitelistLoading } = useAsyncEffect(async () => {
    const normalizedPhone = normalizePhoneNumber(provider?.billing_phone)
    if (!normalizedPhone) return

    const result = await client
      .from('provider_phone_numbers')
      .select('revoked_at')
      .eq('normalized_phone', normalizedPhone)
      .maybeSingle()

    if (result.error) throw result.error
    setIsWhitelisted(!present(result.data?.revoked_at))
  }, [provider?.billing_phone])

  // Get conversations for this provider
  const conversations = useAppSelector((state) =>
    selectConversationsWithNestedDataForProvider(state, provider?.id || '')
  )

  // Get the existing itemized-bill conversation for this expense. Keyed on the incident --
  // see findItemizedBillConversation for why, and for what this screen used to do instead.
  const existingConversation = useMemo((): ConversationWithMessagesAndPhoneCalls | undefined =>
    findItemizedBillConversation(conversations, expense, provider?.id),
    [conversations, expense, provider?.id])

  const providerPhone = provider?.billing_phone
  const isAuthorized = present(provider?.communication_authorized_at)
  const hasOpenItemizedBillTodo = useAppSelector(
    (s) =>
      !!selectTodosForExpense(expenseId || '')(s)
        .filter(isOpenTodo)
        .find((todo) => todo.todo_type === 'itemized-bill')
  )

  const disabledReason = getDisabledReason(
    providerPhone,
    isAuthorized,
    isWhitelisted,
    hasOpenItemizedBillTodo,
    expense?.date
  )
  const isEnabled = disabledReason === null

  const requirements = buildRequirements(
    providerPhone,
    isAuthorized,
    isWhitelisted,
    hasOpenItemizedBillTodo,
    expense?.date
  )

  const handleParameterChange = useCallback((key: string, value: string) => {
    setParameters((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleRequestCall = useCallback(async () => {
    if (!provider || !expense || !expenseId) return

    // Shows the subscribe modal and stops here when the user has no plan.
    if (!checkSubscription()) return

    setIsRequesting(true)

    try {
      const normalizedPhone = normalizePhoneNumber(providerPhone)
      if (!normalizedPhone) {
        throw new Error('Invalid phone number format')
      }

      if (!expense.incident_id) {
        throw new Error('Expense is not attached to an incident')
      }

      const now = new Date().toISOString()
      let conversationId = existingConversation?.id

      // Everything below rides the sync queue rather than going straight to PostgREST.
      // The queue is FIFO and SyncAuthorizer::recordCreated() trusts a row the same batch
      // just inserted, so the phone call may reference a conversation created a line
      // earlier. It also means a failed request is retried rather than lost -- the old
      // direct writes had no transaction, so a phone_calls failure left an orphaned
      // conversation behind and showed the member a toast.
      if (!conversationId) {
        conversationId = uuidv4()

        dispatch(
          addConversation({
            id: conversationId,
            membership_id: membershipId,
            provider_id: provider.id,
            topic_type: 'itemized-bill',
            // The incident, not the expense -- see the existingConversation note above.
            topic_ref_id: expense.incident_id,
            status: 'OPEN',
            created_at: now,
            updated_at: now,
            deleted_at: null,
            last_message_at: null,
            last_message_direction: null,
          })
        )
      }

      // Build final parameters from editable state
      const finalParameters: Record<string, string> = {}
      for (const [key, value] of Object.entries(parameters)) {
        if (value) finalParameters[key] = value
      }

      dispatch(
        addPhoneCall({
          id: uuidv4(),
          conversation_id: conversationId,
          membership_id: membershipId,
          to_phone: normalizedPhone,
          requested_at: now,
          parameters: finalParameters,
          created_at: now,
          updated_at: now,
          deleted_at: null,
          // Server-owned, every one of them. `status` and `initiated_by` are derived in
          // SyncApplier::insert() and the rest are written by the Retell webhooks; they
          // are set here only so the optimistic row renders like the one that syncs back.
          status: 'REQUESTED',
          initiated_by: userId ?? null,
          from_phone: null,
          external_call_id: null,
          duration_seconds: null,
          transcript: null,
          error: null,
          analysis_outcome: null,
          analysis_summary: null,
          analyzed_at: null,
        })
      )

      dispatch(
        updateConversation({
          id: conversationId,
          updated_at: now,
          last_message_at: now,
          last_message_direction: 'OUTBOUND',
        })
      )

      // The modern expense -> conversation link. Without it expenseTable.tsx falls back to
      // matching topic_ref_id against the expense id, which stops working now that
      // topic_ref_id holds the incident.
      if (expense.itemized_bill_conversation_id !== conversationId) {
        dispatch(
          updateExpense({
            id: expense.id,
            updated_at: now,
            itemized_bill_conversation_id: conversationId,
          })
        )
      }

      toast.success('AI phone call requested successfully')
      navigate(`/providers/${providerId}`)
    } catch (error) {
      console.error('Error requesting phone call:', error)

      sentry.captureException(error, {
        tags: {
          component: 'AICallScreen',
          action: 'request_phone_call',
        },
        extra: {
          expenseId,
          providerId,
          providerPhone,
        },
      })

      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to request phone call: ${errorMessage}`)
    } finally {
      setIsRequesting(false)
    }
  }, [
    provider,
    expense,
    providerPhone,
    existingConversation,
    dispatch,
    membershipId,
    expenseId,
    parameters,
    userId,
    navigate,
    providerId,
    sentry,
    checkSubscription,
  ])

  if (!isAiPhoneCallingEnabled) {
    return <NotFound />
  }

  if (!provider || !expense || !providerId || !expenseId) {
    return <NotFound />
  }

  const parameterLabels: Record<string, string> = {
    patient_name: 'Patient Name',
    provider_name: 'Provider Name',
    date_of_service: 'Date of Service',
    total_amount: 'Total Amount',
    patient_dob: 'Patient Date of Birth',
    patient_email: 'Patient Email',
    patient_phone: 'Patient Phone',
    patient_address: 'Patient Address',
  }

  // Parameters that should use a textarea instead of single-line input
  const multilineParameters = new Set(['patient_address'])

  // Parameters that should use a date picker
  const dateParameters = new Set(['patient_dob', 'date_of_service'])

  return (
    <div className="ai-call-screen">
      <h1 className="mb-3">AI call to {provider.name}</h1>

      <div className="card mb-4">
        <div className="card-body">
          <p>
            Request an AI-powered phone call to contact <strong>{provider.name}</strong> and
            request an itemized bill for your expense. The AI will call the provider on your
            behalf and attempt to get the documentation you need.
          </p>
        </div>
      </div>

      {isWhitelistLoading && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {!isWhitelistLoading && !isEnabled && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Requirements</h5>
          </div>
          <div className="card-body">
            <ul className="list-unstyled mb-0">
              {requirements.map((req) => (
                <li key={req.id} className="mb-3">
                  <div className="d-flex align-items-start">
                    <i
                      className="material-icons me-2"
                      style={{
                        color: req.satisfied ? '#28a745' : '#dc3545',
                        fontSize: '20px',
                      }}
                    >
                      {req.satisfied ? 'check_circle' : 'cancel'}
                    </i>
                    <div>
                      <strong>{req.label}</strong>
                      <div className="text-muted small">{req.explanation}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!isWhitelistLoading && isEnabled && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Call Details</h5>
          </div>
          <div className="card-body">
            <p className="text-muted small mb-3">
              The AI will reference these details if the provider asks for information during the call.
            </p>
            <table className="table table-sm mb-0">
              <tbody>
                {Object.entries(parameterLabels).map(([key, label]) => (
                  <tr key={key}>
                    <td className="fw-bold" style={{ width: '40%', verticalAlign: 'top' }}>
                      {label}
                    </td>
                    <td>
                      {multilineParameters.has(key) ? (
                        <textarea
                          className="form-control form-control-sm"
                          rows={3}
                          value={parameters[key] || ''}
                          placeholder={`Enter ${label.toLowerCase()}`}
                          onChange={(e) => handleParameterChange(key, e.target.value)}
                        />
                      ) : dateParameters.has(key) ? (
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={parameters[key] || ''}
                          onChange={(e) => handleParameterChange(key, e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={parameters[key] || ''}
                          placeholder={`Enter ${label.toLowerCase()}`}
                          onChange={(e) => handleParameterChange(key, e.target.value)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-center">
        <button
          className="btn btn-primary"
          onClick={handleRequestCall}
          disabled={isWhitelistLoading || !isEnabled || isRequesting}
        >
          {isRequesting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Requesting...
            </>
          ) : (
            <>
              <i className="material-icons me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>
                phone
              </i>
              Request AI Call
            </>
          )}
        </button>
      </div>

      <div className="text-center mt-4">
        <p className="text-muted small mb-0">
          By requesting an AI call, you acknowledge that HealthShare Technology Solutions may
          review phone call transcripts for AI training and quality improvement purposes.
        </p>
      </div>

      <SubscriptionPromptModal />
    </div>
  )
}
